/**
 * The file a family takes away — **one self-contained HTML document**, built the same way on
 * every surface that offers it (issue #304; ADR 0020 decision 1's re-visit trigger; shared
 * since issue #388).
 *
 * ## Why it is here
 *
 * It was `apps/my.iamboone.app/src/export/document.ts`, and ADR 0025 decision 4 named what this
 * package would carry once mobile was a real second caller: *"the client that consumes them
 * and the formatting both surfaces share"*. Boone's app shares the archive read and hands the
 * same file to the share sheet, so this is that formatting — moved, not copied, because two
 * renderers of the one document whose whole claim is completeness could only ever disagree.
 *
 * ## The one shape it declares, and why that is a bound and not a body
 *
 * `index.ts` forbids this package a response shape of its own. {@link ArchiveLike} is not
 * one: it is the **formatter's input contract**, the least a caller has to hand over, and it
 * is exported the way `WriteEnvelope` is — as a bound. The server's derived `GET /archive`
 * payload satisfies it, and each surface's typecheck is what proves that, by passing its own
 * derived type in. A field the server adds reaches the document with no edit here, because the
 * renderer walks `fields` rather than naming them (below); a field the server *renames* fails
 * the caller's build, not silently this one.
 *
 * ## Why HTML, when the projection is already JSON
 *
 * ADR 0020 could not offer a file at all, so it defined *portable* as *"a structured document
 * you can copy"*. The obvious ways to make good on it are both developer formats wearing a
 * family's name: JSON is the mechanism this product exists to hide, and Markdown opened in
 * Notepad is asterisks. What a family means by *a copy of our things* is something that opens
 * — on every operating system, with nothing installed, searchable, printable to PDF through the
 * browser's own dialog, and survivable in an email because it has no assets.
 *
 * **And it is the machine-readable artefact too.** {@link DATA_ELEMENT_ID} carries the same
 * projected records as JSON in a non-executing `<script type="application/json">` block. A
 * family sees a document; anything that wants the data reads the block.
 *
 * ## It walks the projection rather than naming its fields
 *
 * {@link fieldLabel} de-camelCases whatever key arrives. That is the whole reason a field added
 * to the server's projection reaches this file with no edit here — and, more importantly, why
 * one *cannot* be silently missed. A label table would be a tenth projection maintained by
 * hand, on the one surface whose entire claim is that nothing is left out.
 *
 * ## The mark is the asset
 *
 * The masthead inlines `@pilotfish/brand`'s `MARK_180_DATA_URI` — the canonical bytes, held to
 * `assets/icon-180.png` by that package's own invariant — never a drawn approximation. The
 * panel used to inline its served copy through Vite; a phone has no Vite, so the package
 * carries the value both can reach.
 *
 * ## Nothing here decides what the file contains
 *
 * The projection, the section labels, the `doesNotInclude` sentences and the `unreadable` count
 * are all the server's. This formats them. The one rule it enforces on its own is the
 * completeness sentence, which **disappears whenever `unreadable` is above zero** — a partial
 * copy that still says *"that's the lot"* is the inventory lying.
 */

import { MARK_180_DATA_URI } from "@pilotfish/brand";

/** One record inside a section — an id and whatever the projection put on it. */
export interface ArchiveEntryLike {
  readonly id: string;
  readonly fields: Readonly<Record<string, unknown>>;
}

/** One titled group. */
export interface ArchiveSectionLike {
  readonly label: string;
  readonly entries: readonly ArchiveEntryLike[];
}

/** The least the formatter needs — the bound the server's own payload satisfies. */
export interface ArchiveLike {
  readonly snapshotAt: string;
  readonly sections: readonly ArchiveSectionLike[];
  readonly totalRecords: number;
  readonly unreadable: number;
  readonly doesNotInclude: readonly string[];
}

/** Where the same records ride as data inside the readable file. */
export const DATA_ELEMENT_ID = "boone-archive-data";

/**
 * HTML-escape a value that came from a family.
 *
 * **Every string in this document is family-authored** — a fact's title, an item's `rawText`, a
 * feedback message — so this is the only thing standing between a `<` somebody typed and a file
 * that renders wrongly or, worse, executes. Applied at the one place values become markup.
 * `&` first, or the escapes escape each other.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** `yourWords` → `Your words`. Total over whatever key the projection emits. */
export function fieldLabel(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .toLowerCase()
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * What a field's value reads as, as lines.
 *
 * An array becomes several lines because that is what `proposed`, `applied` and `describes`
 * are. An empty array and a `null` are both {@link NOTHING} rather than blank: an absent value
 * a family can see is information, and a blank cell is indistinguishable from a bug in the file.
 */
function valueLines(value: unknown): readonly string[] {
  if (value === null || value === undefined) return [NOTHING];
  if (Array.isArray(value)) {
    const lines = value.map((one) => (typeof one === "string" ? one : JSON.stringify(one)));
    return lines.length === 0 ? [NOTHING] : lines;
  }
  if (typeof value === "string") return value.length === 0 ? [NOTHING] : [value];
  if (typeof value === "number" || typeof value === "boolean") return [String(value)];
  return [JSON.stringify(value)];
}

const NOTHING = "—";

/** The entry's own heading — the first field's value; the rows below repeat it. */
function entryHeading(entry: ArchiveEntryLike): string {
  const first = Object.values(entry.fields)[0];
  const [line] = valueLines(first);
  return line === undefined || line === NOTHING ? entry.id : line;
}

/** `The Riveras` → `the-riveras`, for a filename. */
function slug(text: string): string {
  const cleaned = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return cleaned.length === 0 ? "family" : cleaned;
}

/** `2026-08-24`, from the server's own reading of the record — when the read *finished*. */
function isoDate(snapshotAt: string): string {
  const at = new Date(snapshotAt);
  return Number.isNaN(at.getTime()) ? "export" : (at.toISOString().split("T")[0] ?? "export");
}

/** What the download is called. Dated, so two copies a month apart do not overwrite. */
export function fileName(familyName: string | null, snapshotAt: string): string {
  return `boone-${slug(familyName ?? "family")}-${isoDate(snapshotAt)}.html`;
}

/** When the file says it was read, in words rather than an ISO instant. */
function readableInstant(snapshotAt: string): string {
  const at = new Date(snapshotAt);
  if (Number.isNaN(at.getTime())) return snapshotAt;
  return at.toLocaleString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * The sentence under the title.
 *
 * **The completeness clause and the count travel together, and both go when a row could not be
 * read.** A file that says *"everything"* while missing rows is worse than one that says what
 * it is missing.
 */
export function summaryLine(archive: ArchiveLike): string {
  const { totalRecords, unreadable } = archive;
  if (totalRecords === 0 && unreadable === 0) {
    return "There is nothing saved for your family yet, so this copy is genuinely empty rather than short.";
  }
  const counted = `${totalRecords} ${totalRecords === 1 ? "thing" : "things"} in all`;
  if (unreadable > 0) {
    return (
      `${counted}, and ${unreadable} ${unreadable === 1 ? "record" : "records"} ` +
      `Boone holds for you could not be read back, so ${unreadable === 1 ? "it is" : "they are"} ` +
      "missing from this copy. Everything else is here."
    );
  }
  return `${counted}. That's the lot; nothing is held back.`;
}

/**
 * The same records as data, safe to embed. `<` is escaped to its JSON form so the payload
 * cannot close the element it sits in.
 */
function dataBlock(archive: ArchiveLike): string {
  return JSON.stringify(archive, null, 2).replace(/</g, "\\u003c");
}

function renderEntry(entry: ArchiveEntryLike): string {
  const rows = Object.entries(entry.fields)
    .map(([key, value]) => {
      const lines = valueLines(value)
        .map((line) => `<p class="v">${escapeHtml(line)}</p>`)
        .join("");
      return `<div class="row"><dt>${escapeHtml(fieldLabel(key))}</dt><dd>${lines}</dd></div>`;
    })
    .join("");
  return `<article class="entry"><h3>${escapeHtml(entryHeading(entry))}</h3><dl>${rows}</dl></article>`;
}

function renderSection(section: ArchiveSectionLike): string {
  return [
    `<section class="sec">`,
    `<h2>${escapeHtml(section.label)}<span class="n">${section.entries.length}</span></h2>`,
    section.entries.map(renderEntry).join(""),
    `</section>`,
  ].join("");
}

/**
 * The whole file, as one string — it has to be serialisable, and every value that reaches it
 * goes through {@link escapeHtml} on one line, where the rule is visible.
 *
 * **`@media print` forces the light palette.** A browser in dark mode prints backgrounds off by
 * default, so a dark document prints as pale grey text on white.
 */
export function buildArchiveDocument(archive: ArchiveLike, familyName: string | null): string {
  const title = `Everything Boone holds about ${familyName ?? "your family"}`;
  const omissions = archive.doesNotInclude.map((line) => `<li>${escapeHtml(line)}</li>`).join("");

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)}</title>
<style>
/* The product's tokens (@pilotfish/brand). Same names as every surface's :root, so the two
   can be diffed by eye. */
:root {
  --bg: #f6f4ee; --surface: #fffdf8; --ink: #0d1b2a; --ink-soft: #46586b;
  --rule: #e2dccc; --brand: #1e3a2e; --accent: #c56a2d; --muted: #f1efe6;
}
@media (prefers-color-scheme: dark) {
  :root {
    --bg: #0f1a14; --surface: #16241c; --ink: #ece7da; --ink-soft: #a9b5ab;
    --rule: #26382c; --brand: #a3b18a; --accent: #e08a4e; --muted: #1c2b22;
  }
}
* { box-sizing: border-box; }
body {
  margin: 0; padding: 32px 20px 64px; background: var(--bg); color: var(--ink);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 15px; line-height: 1.55;
}
main { max-width: 52rem; margin: 0 auto; }
.lockup { display: flex; align-items: center; gap: 11px; margin-bottom: 26px; }
.mark { width: 34px; height: 34px; border-radius: 11px; flex: none; }
.names { display: flex; flex-direction: column; line-height: 1.05; }
.wordmark, .handle, h1, h2, h3, .n {
  font-family: "Poppins", "Century Gothic", "Futura", system-ui, sans-serif;
}
.wordmark { font-weight: 700; font-size: 19px; letter-spacing: -0.02em; }
.handle { font-weight: 600; font-size: 10.5px; color: var(--accent); }
h1 { font-size: 26px; font-weight: 600; letter-spacing: -0.01em; margin: 0 0 8px; }
.lede { margin: 0 0 4px; color: var(--ink-soft); font-size: 14px; }
.short {
  margin: 18px 0 0; padding: 14px 16px; border-radius: 14px;
  background: var(--muted); border: 1px solid var(--rule); font-size: 14px;
}
.sec { margin-top: 34px; }
h2 {
  display: flex; align-items: baseline; gap: 10px; font-size: 13px; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink-soft);
  margin: 0 0 10px; padding-bottom: 8px; border-bottom: 1px solid var(--rule);
}
.n { font-size: 13px; color: var(--ink-soft); letter-spacing: 0; }
.entry {
  background: var(--surface); border: 1px solid var(--rule); border-radius: 14px;
  padding: 16px 18px; margin-bottom: 10px;
}
.entry h3 { font-size: 15px; font-weight: 600; margin: 0 0 10px; }
dl { margin: 0; display: flex; flex-direction: column; gap: 6px; }
.row { display: grid; grid-template-columns: 13rem 1fr; gap: 14px; }
dt { color: var(--ink-soft); font-size: 13px; }
dd { margin: 0; font-size: 14px; overflow-wrap: anywhere; }
.v { margin: 0 0 2px; }
.not { margin-top: 34px; padding-top: 18px; border-top: 1px solid var(--rule); }
.not h2 { border: 0; padding: 0; }
.not ul { margin: 0; padding-left: 1.1rem; display: flex; flex-direction: column; gap: 8px; }
.not li { color: var(--ink-soft); font-size: 14px; }
.foot { margin-top: 30px; color: var(--ink-soft); font-size: 13px; }
@media (max-width: 34rem) { .row { grid-template-columns: 1fr; gap: 2px; } }
@media print {
  :root {
    --bg: #ffffff; --surface: #ffffff; --ink: #0d1b2a; --ink-soft: #46586b;
    --rule: #cccccc; --brand: #1e3a2e; --accent: #c56a2d; --muted: #f4f4f4;
  }
  body { padding: 0; font-size: 11pt; }
  .entry, .sec { break-inside: avoid; }
}
</style>
</head>
<body>
<main>
  <div class="lockup">
    <img class="mark" src="${MARK_180_DATA_URI}" alt="I am Boone" width="34" height="34">
    <div class="names"><span class="wordmark">iamboone</span><span class="handle">@boone</span></div>
  </div>

  <h1>${escapeHtml(title)}</h1>
  <p class="lede">Read on ${escapeHtml(readableInstant(archive.snapshotAt))}.</p>
  <p class="lede">${escapeHtml(summaryLine(archive))}</p>

  ${archive.sections.map(renderSection).join("\n  ")}

  <div class="not">
    <h2>What this copy does not include</h2>
    <ul>${omissions}</ul>
  </div>

  <p class="foot">
    This file is yours. It also carries the same record as data — open it in a text editor and
    look for <code>${escapeHtml(DATA_ELEMENT_ID)}</code> — so anything you want to move it into
    can read it without retyping.
  </p>
</main>
<script type="application/json" id="${DATA_ELEMENT_ID}">
${dataBlock(archive)}
</script>
</body>
</html>
`;
}

/**
 * The same document as plain text, for *or copy it as text* — the same walk over the same
 * projection, so the two cannot describe different records.
 */
export function buildArchiveText(archive: ArchiveLike, familyName: string | null): string {
  const lines: string[] = [
    `Everything Boone holds about ${familyName ?? "your family"}`,
    `Read on ${readableInstant(archive.snapshotAt)}.`,
    summaryLine(archive),
  ];

  for (const section of archive.sections) {
    lines.push("", `${section.label.toUpperCase()} (${section.entries.length})`);
    for (const entry of section.entries) {
      lines.push("", `  ${entryHeading(entry)}`);
      for (const [key, value] of Object.entries(entry.fields)) {
        for (const line of valueLines(value)) lines.push(`    ${fieldLabel(key)}: ${line}`);
      }
    }
  }

  lines.push("", "WHAT THIS COPY DOES NOT INCLUDE");
  for (const omission of archive.doesNotInclude) lines.push(`  - ${omission}`);
  return `${lines.join("\n")}\n`;
}
